import { useMemo, useRef } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import { InfiniteVirtualTable } from '@nesso/ui/blocks/table/infinite-virtual-table';
import { VirtualTableLoadingRow } from '@nesso/ui/blocks/table/virtual-table-loading-row';

import {
    isSessionLocked,
    type SessionListItem,
    type SessionNoteStatus,
    sessionListOptions,
} from '@/entities/session';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { DataSpinner } from '@/shared/ui/indicators/data-spinner';

import { getRowDecorations } from '../../lib/get-row-decorations';
import { pastSessionsTableColumns } from './past-sessions-table/past-sessions-table-columns';
import { PastSessionsTableError } from './past-sessions-table/past-sessions-table-error';
import { PastSessionsTableHeader } from './past-sessions-table/past-sessions-table-header';
import { PastSessionsTableRow } from './past-sessions-table/past-sessions-table-row';
import { PastSessionsTableSkeleton } from './past-sessions-table/past-sessions-table-skeleton';

const ROW_HEIGHT = 52;
const ROW_WITH_HEADER_HEIGHT = ROW_HEIGHT * 2;

type Props = {
    status?: SessionNoteStatus;
    search?: string;
};

export function PastSessionsTable({ status, search }: Props) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const openSheet = useSheetStore((state) => state.openSheet);

    const query = useInfiniteQuery(
        sessionListOptions({
            status: status,
            search: search,
            exclude_first_generation: true,
        }),
    );

    // React Compiler bails out on this component (IncompatibleLibrary on
    // useReactTable). Memoize manually so the table doesn't rebuild its
    // row model on every render.
    const sessions = useMemo<SessionListItem[]>(
        () => query.data?.pages?.flatMap((p) => p.items) ?? [],
        [query.data],
    );
    const decorations = useMemo(() => getRowDecorations(sessions), [sessions]);

    const table = useReactTable({
        data: sessions,
        columns: pastSessionsTableColumns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id,
    });

    return match(query.status)
        .with('error', () => <PastSessionsTableError />)
        .otherwise(() => (
            <InfiniteVirtualTable<SessionListItem>
                {...query}
                table={table}
                rows={table.getRowModel().rows}
                scrollContainerRef={scrollContainerRef}
                className="flex-1 overflow-auto min-h-0"
                showHeader={false}
                estimateSize={(index) =>
                    decorations[index]?.header ? ROW_WITH_HEADER_HEIGHT : ROW_HEIGHT
                }
                overscan={10}
                emptyMessage="No results found"
                loadingRow={
                    <VirtualTableLoadingRow
                        colSpan={pastSessionsTableColumns.length}
                        spinner={<DataSpinner />}
                        rowClassName="table-row"
                        cellClassName="table-cell py-4 text-center"
                    />
                }
                skeleton={<PastSessionsTableSkeleton />}
                renderRow={({ row, colSpan }) => {
                    const decoration = decorations[row.index];
                    const session = row.original;
                    const isLocked = isSessionLocked(session);
                    return (
                        <>
                            {decoration?.header && (
                                <PastSessionsTableHeader
                                    colSpan={colSpan}
                                    weekday={decoration.header.weekday}
                                    dayAndMonth={decoration.header.dayAndMonth}
                                    isDayCompleted={decoration.header.isDayCompleted}
                                />
                            )}
                            <PastSessionsTableRow
                                row={row}
                                hideRowBorder={decoration?.isLastOfDay ?? false}
                                disabled={isLocked}
                                onClick={() => {
                                    if (isLocked) return;
                                    openSheet({
                                        type: 'sessions/details',
                                        label: 'Session Note',
                                        data: session,
                                    });
                                }}
                            />
                        </>
                    );
                }}
            />
        ));
}
